import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import { IRegEdSubmissionView } from './types';

export const REGED_SUBMISSIONS_QUERY_KEY = 'regEdSubmissions';

/**
 * Newest-first; each row is a submission *chain* (a revision repoints the same row), so the first
 * element is the parent's latest submission state.
 */
export const useRegEdSubmissionsQuery = ({
	parentId,
	enabled = true,
	impersonationContext,
}: {
	parentId: string | null | undefined;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) => {
	const userSession = useUserSession();
	return useQuery({
		enabled: enabled && !!parentId,
		queryKey: [REGED_SUBMISSIONS_QUERY_KEY, parentId, impersonationContext?.account?.id],
		queryFn: () =>
			userSession.webServiceHelper.callAsync<IRegEdSubmissionView[]>({
				path: Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: { parentId: parentId! },
					urlPath: 'compliance/regEd/submissions',
				}),
				method: 'GET',
				// A non-admin gets 404 for a parent they didn't create, and it deliberately doesn't
				// distinguish that from "no such parent". A colleague's campaign is nothing to show, not an
				// error — the positional form would throw, retry, and toast.
				throwOn404: false,
			}),
		meta: {
			toastErrorMessage: 'Failed to load the compliance submission status.',
		},
	});
};

export const invalidateRegEdSubmissions = (parentId?: string) => {
	return reactQueryClient.invalidateQueries({
		queryKey: parentId ? [REGED_SUBMISSIONS_QUERY_KEY, parentId] : [REGED_SUBMISSIONS_QUERY_KEY],
	});
};
